import React, { useRef } from 'react';
import {
  TextField,
  Checkbox,
  FormControlLabel,
  FormHelperText,
  Box,
  InputLabel,
  Select,
  MenuItem,
  FormControl,
  Button,
} from '@mui/material';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import FileUpload from './UIElements/FileUpload';
import { useDispatch } from 'react-redux';
import {
  OtherstransactionIdCheck,
  transactionIdCheck,
} from '../features/payments/PaymentSlice';

const AddFeePayment = ({
  onClose,
  onSubmission,
  balance,
  writeOff = true,
  isSubmitting = false,
  usedFor = 'Fee',
}) => {
  const fileInputRef = useRef();
  const dispatch = useDispatch();
  const formik = useFormik({
    initialValues: {
      amount: '',
      transactionReference: '',
      paymentType: '',
      supportingDocument: null,
      writeOffRemaining: false,
    },
    validationSchema: Yup.object({
      transactionReference: Yup.string()
        .trim()
        .required('Transaction reference is required'),
      paymentType: Yup.string().required('Select PaymentType'),
      amount: Yup.number()
        .required('Amount is required')
        .min(1, 'Amount must positive value')
        .max(balance, `Do not enter more than Balance Amount ${balance}`),
      supportingDocument: Yup.mixed().nullable(),
      writeOffRemaining: Yup.boolean(),
    }),
    context: { dispatch },
    validateOnBlur: true,
    validateOnChange: true,
    onSubmit: async (values) => {
      const formData = {
        amount: values.amount,
        transactionReference: values.transactionReference,
        paymentType: values.paymentType,
        supportingDocument: values.supportingDocument,
        writeOffRemaining:
          values.amount === balance ? false : values.writeOffRemaining,
      };
      let status = await onSubmission(formData);
      if (status) {
        formik.resetForm();
        onClose();
        if (fileInputRef.current) fileInputRef.current.value = '';
      }
    },
  });

  return (
    <form onSubmit={formik.handleSubmit} noValidate>
      <TextField
        fullWidth
        margin='normal'
        id='transactionReference'
        name='transactionReference'
        label='Transaction Reference'
        className='formik-input'
        value={formik.values.transactionReference}
        onChange={formik.handleChange}
        error={
          formik.touched.transactionReference &&
          Boolean(formik.errors.transactionReference)
        }
        helperText={
          formik.touched.transactionReference &&
          formik.errors.transactionReference
        }
        onBlur={async (e) => {
          formik.handleBlur(e);
          if (e.target.value) {
            let error = document.getElementById('transactionIdError');
            let isDuplicate;
            if (usedFor === 'Fee') {
              isDuplicate = await dispatch(
                transactionIdCheck(e.target.value),
              ).unwrap();
            } else {
              isDuplicate = await dispatch(
                OtherstransactionIdCheck(e.target.value),
              ).unwrap();
            }
            isDuplicate
              ? (error.innerHTML = '<p>Transaction ID already Exists</p>')
              : (error.innerHTML = '');
          }
        }}
        onMouseLeave={async (e) => {
          if (e.target.value) {
            let error = document.getElementById('transactionIdError');
            let isDuplicate;
            if (usedFor === 'Fee') {
              isDuplicate = await dispatch(
                transactionIdCheck(e.target.value),
              ).unwrap();
            } else {
              isDuplicate = await dispatch(
                OtherstransactionIdCheck(e.target.value),
              ).unwrap();
            }
            isDuplicate
              ? (error.innerHTML = '<p>Transaction ID already Exists</p>')
              : (error.innerHTML = '');
          }
        }}
      />{' '}
      <div id='transactionIdError' className='text-red-400 mt-1'></div>
      <FormControl
        fullWidth
        error={Boolean(formik.touched.paymentType && formik.errors.paymentType)}
      >
        <InputLabel id='paymentType'>Payment Type</InputLabel>
        <Select
          name='paymentType'
          fullWidth
          labelId='paymentType'
          id='demo-simple-select'
          className='w-full'
          value={formik.values.paymentType}
          label='Payment Type'
          onBlur={formik.handleBlur}
          onChange={formik.handleChange}
        >
          <MenuItem value='' disabled>
            Select All
          </MenuItem>
          <MenuItem value='Cash'>Cash</MenuItem>
          <MenuItem value='Online(UPI)'>Online(UPI)</MenuItem>
          <MenuItem value='Card'>Card</MenuItem>
        </Select>
        {formik.touched.paymentType && formik.errors.paymentType && (
          <FormHelperText>{formik.errors.paymentType}</FormHelperText>
        )}
      </FormControl>
      <TextField
        fullWidth
        margin='normal'
        id='amount'
        name='amount'
        label='Amount'
        type='number'
        className='formik-input'
        inputProps={{ min: 1 }}
        value={formik.values.amount}
        onBlur={formik.handleBlur}
        onChange={formik.handleChange}
        error={formik.touched.amount && Boolean(formik.errors.amount)}
        helperText={formik.touched.amount && formik.errors.amount}
      />
      {false && (
        <FileUpload
          onFileSelect={(file) => {
            formik.setFieldValue('supportingDocument', file);
          }}
          fileName={
            formik.values.supportingDocument
              ? formik.values.supportingDocument.name
              : ''
          }
          error={
            formik.touched.supportingDocument &&
            Boolean(formik.errors.supportingDocument)
          }
          helperText={
            formik.touched.supportingDocument &&
            formik.errors.supportingDocument
          }
        />
      )}
      {formik.touched.supportingDocument &&
        formik.errors.supportingDocument && (
          <FormHelperText error>
            {formik.errors.supportingDocument}
          </FormHelperText>
        )}
      {formik.values.amount > 0 && writeOff && (
        <FormControlLabel
          control={
            <Checkbox
              id='writeOffRemaining'
              name='writeOffRemaining'
              checked={
                formik.values.amount < balance
                  ? formik.values.writeOffRemaining
                  : false
              }
              disabled={formik.values?.amount === balance}
              onChange={formik.handleChange}
              color='primary'
            />
          }
          label='Write off the remaining balance'
        />
      )}
      <Box className='flex justify-end gap-2'>
        <button
          className='bg-gray-300 text-gray-700 px-4 py-2 rounded hover:bg-gray-400'
          onClick={() => onClose()}
          type='button'
        >
          Cancel
        </button>

        <Button variant='contained' disabled={isSubmitting} type='submit'>
          Submit
        </Button>
      </Box>
    </form>
  );
};

export default AddFeePayment;
